/**
 * Product Store (Zustand)
 *
 * Manages product catalog with caching
 */

import { create } from 'zustand';
import type { Product } from '@/types';

interface ProductStore {
  products: Product[];
  loading: boolean;
  error: string | null;
  lastFetched: number | null;

  // Actions
  setProducts: (products: Product[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;

  // Helpers
  getProductById: (id: string) => Product | undefined;
  getProductBySlug: (slug: string) => Product | undefined;
  getProductsByCategory: (category: string) => Product[];
  getFeaturedProducts: () => Product[];
}

export const useProductStore = create<ProductStore>((set, get) => ({
  products: [],
  loading: false,
  error: null,
  lastFetched: null,

  setProducts: (products) => {
    set({
      products,
      lastFetched: Date.now(),
      error: null,
    });
  },

  setLoading: (loading) => {
    set({ loading });
  },

  setError: (error) => {
    set({ error, loading: false });
  },

  getProductById: (id) => {
    const { products } = get();
    return products.find((p) => p.id === id);
  },

  getProductBySlug: (slug) => {
    const { products } = get();
    return products.find((p) => p.slug === slug);
  },

  getProductsByCategory: (category) => {
    const { products } = get();
    return products.filter((p) => p.category === category);
  },

  getFeaturedProducts: () => {
    const { products } = get();
    return products.filter((p) => p.isFeatured);
  },
}));
